/**
 * The explorer's **default view** — client specification S3, first clause.
 *
 * > *"The explorer opens on the latest available biophysical land cover across the
 * > four-country extent, with a land-cover time series behind a time slider."*
 *
 * Renders nothing. It is one write, once, on the first load that has nothing to show:
 * select the theme whose default is a **series**, and let `applyThemeDefault` put that
 * theme's default dataset on the map. Which theme that is comes from
 * `/meta/config.theme_defaults` — the entry carrying a non-null `series` — so the id
 * `lc_annual`, the domain `landuse` and the years 2017–2024 are all the server's and none
 * of them is written here. A client who re-points the Land Use default at the ICRAF product
 * changes one table on the server and this opens on that instead.
 *
 * ## What "has nothing to show" means, and why it is checked
 *
 * A permalink is a record of a map somebody chose, and it is applied before React mounts
 * (`main.tsx`). So the default view runs only when the store is still empty of both kinds
 * of mapping — no indicator, no layers — **and** of a rail pick (`domFilter` is still the
 * boot `''`), and only once the two catalogues have actually answered. Every one of those
 * is "do not act": opening a shared link on land cover the sender never chose would be the
 * same class of mistake as the `b21` cold-load default this replaces, and re-pointing the
 * rail under a reader who reached it before the catalogues answered is the gesture-eating
 * variant of it.
 *
 * It also waits for `PermalinkSync` to finish confirming the ids a hash carried, for the
 * reason `OperationalHierarchy` does: the hash's indicator may be on its way *out*, and
 * acting in the window between would put a raster under a choropleth that is about to be
 * dropped.
 */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchConfig, fetchIndicators, fetchLayers, queryKeys } from '../../lib/api';
import { pendingPermalinkIds } from '../../lib/permalink';
import { useAppStore } from '../../lib/state';
import { applyThemeDefault } from '../sidebar/applyTheme';
import { defaultViewTheme, domFilterOf } from '../sidebar/themes';
import { defaultViewOpened, markDefaultViewOpened } from './defaultViewOnce';

export function DefaultView() {
  const role = useAppStore((s) => s.role);

  const { data: config } = useQuery({ queryKey: queryKeys.config(), queryFn: fetchConfig });
  const { data: layers } = useQuery({ queryKey: queryKeys.layers(role), queryFn: fetchLayers });
  const { data: indicators } = useQuery({
    queryKey: queryKeys.indicators(),
    queryFn: fetchIndicators,
  });

  useEffect(() => {
    if (defaultViewOpened()) return;
    if (!config || !layers || !indicators) return;
    if (pendingPermalinkIds().indicators.length) return;

    const state = useAppStore.getState();
    // Somebody's map already — a permalink, or a reader who got here first. The third
    // check is the reader's *rail pick*: `domFilter` boots as `''` and only a real
    // gesture moves it (a permalink never carries it), so a non-empty value means the
    // reader reached the rail before the catalogues answered. Acting anyway would yank
    // their pane over to Land Use — on a slow stack, exactly what happened to a reader
    // who opened the Indicators icon straight off the boot.
    if (state.indicator || state.layersOn.size > 0 || state.domFilter) {
      markDefaultViewOpened();
      return;
    }
    const theme = defaultViewTheme(config);
    if (!theme) return;

    markDefaultViewOpened();
    state.setDomFilter(domFilterOf(theme));
    applyThemeDefault(theme, { config, layers, indicators });
  }, [config, layers, indicators]);

  return null;
}

export default DefaultView;
